'use client';

import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { CtaButton } from '@/components/ds';
import { useSignupModeState } from '@/components/utils/SignupModeProvider';
import { useHideCustomerlyWhileMounted } from '@/components/utils/customerly/useHideCustomerlyWhileMounted';
import { useAskAction } from './useNewsVisitor';

const SHOW_AFTER = 0.3;
const HIDE_AFTER = 0.88;

const Bar = ({ slug, onDismiss }: { slug: string; onDismiss: () => void }) => {
  // La chat starebbe proprio sopra il bottone
  useHideCustomerlyWhileMounted();
  const action = useAskAction(slug, 'sticky')(null);

  return (
    <div className="fixed inset-x-0 bottom-0 z-40 border-t border-edge bg-white/95 px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] shadow-[0_-10px_40px_rgb(0_25_55/0.12)] backdrop-blur lg:hidden">
      <div className="mx-auto flex max-w-[40rem] items-center gap-3">
        <p className="min-w-0 flex-1 text-[0.84375rem] leading-[1.35] font-medium text-primary">
          E nel caso del tuo cliente? Chiedilo con le fonti.
        </p>
        <CtaButton href={action.href} onClick={action.onClick} size="sm" className="shrink-0">
          {action.label}
        </CtaButton>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Chiudi"
          className="grid h-8 w-8 shrink-0 place-items-center rounded-ds-sm text-ink-subtle ds-focus hover:text-primary"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};

/**
 * Barra fissa in fondo, solo sotto `lg` (su desktop la CTA è già nella colonna
 * che segue lo scroll). Compare quando la lettura è avviata e si toglie prima
 * della chiusura, dove il riquadro «Chiedilo» è già in pagina: mai due CTA
 * uguali nello stesso schermo.
 */
const NewsStickyCta = ({ slug }: { slug: string }) => {
  const { isSettled } = useSignupModeState();
  const [inRange, setInRange] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      const progress = scrollable > 0 ? window.scrollY / scrollable : 0;
      setInRange(progress > SHOW_AFTER && progress < HIDE_AFTER);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  if (!isSettled || !inRange || dismissed) return null;
  return <Bar slug={slug} onDismiss={() => setDismissed(true)} />;
};

export default NewsStickyCta;
